/**
 * apiErrorText — turn an axios error's response body into a plain
 * human-readable string, regardless of what FastAPI decided to send.
 *
 * FastAPI's `detail` can be:
 *   - a string (typical for HTTPException) — return as-is
 *   - an array of Pydantic v2 validation-error objects
 *     ([{type, loc, msg, input, url}, ...]) — join their `msg` fields
 *   - a single object — try `.msg`, else `JSON.stringify`
 *   - undefined — return the provided fallback
 *
 * Rendering the raw array as a React child crashes with
 * "Objects are not valid as a React child", so every UI that surfaces
 * an axios error to the user goes through this helper.
 */
export function apiErrorText(err, fallback = "Something went wrong") {
  const d = err?.response?.data?.detail;
  if (d == null) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const parts = d
      .map((row) => {
        if (typeof row === "string") return row;
        if (row && typeof row === "object") {
          const loc = Array.isArray(row.loc)
            ? row.loc.filter((x) => x !== "body").join(".")
            : "";
          const msg = row.msg || row.message || "";
          return loc ? `${loc}: ${msg}` : msg;
        }
        return "";
      })
      .filter(Boolean);
    return parts.join(" · ") || fallback;
  }
  if (typeof d === "object") {
    return d.msg || d.message || JSON.stringify(d);
  }
  return String(d);
}
